'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { formatDT } from '@/lib/utils'
import { ArrowLeft, Search, Plus, User, Phone, ChevronRight, X } from 'lucide-react'
import Link from 'next/link'

export default function ClientsPage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const [clients, setClients] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [form, setForm] = useState({ name: '', phone: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    load()
  }, [session])

  async function load(q?: string) {
    setLoading(true)
    const data = await api.getClients(session!.shop.id, q)
    setClients(data)
    setLoading(false)
  }

  useEffect(() => {
    const t = setTimeout(() => load(search || undefined), 300)
    return () => clearTimeout(t)
  }, [search])

  async function createClient() {
    if (!form.name.trim()) return
    setSaving(true)
    await api.createClient({ shop_id: session!.shop.id, name: form.name.trim(), phone: form.phone.trim() })
    setForm({ name: '', phone: '' })
    setShowNew(false)
    setSaving(false)
    load()
  }

  const totalDette = clients.filter(c => parseFloat(c.solde) < 0).reduce((s, c) => s + Math.abs(parseFloat(c.solde)), 0)
  const totalCredit = clients.filter(c => parseFloat(c.solde) > 0).reduce((s, c) => s + parseFloat(c.solde), 0)

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">{t("clients_title")}</h1>
          <button onClick={() => setShowNew(true)}
            className="w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center active:scale-95">
            <Plus className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="px-4 pb-3 relative">
          <Search className="absolute left-7 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("search")}
            className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:bg-white" />
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Stats */}
        {clients.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
              <p className="text-xs text-green-600 font-medium mb-1">{t("total_credit")}</p>
              <p className="text-xl font-bold text-green-700">{formatDT(totalCredit)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <p className="text-xs text-red-600 font-medium mb-1">{t("total_debt")}</p>
              <p className="text-xl font-bold text-red-700">{formatDT(totalDette)}</p>
            </div>
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-16">
            <User className="w-14 h-14 mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 font-medium">{t("no_data")}</p>
            <button onClick={() => setShowNew(true)}
              className="mt-4 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-semibold active:scale-95">
              + Ajouter un client
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {clients.map(client => {
              const solde = parseFloat(client.solde) || 0
              return (
                <Link key={client.id} href={`/clients/${client.id}`}
                  className="bg-white rounded-2xl px-4 py-4 flex items-center gap-3 shadow-sm border border-gray-100 active:scale-[0.99]">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                    solde < 0 ? 'bg-red-100 text-red-600' : solde > 0 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{client.name}</p>
                    {client.phone && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" />{client.phone}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold text-sm ${solde < 0 ? 'text-red-500' : solde > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {solde > 0 ? '+' : ''}{formatDT(solde)}
                    </p>
                    <p className="text-xs text-gray-400">{solde < 0 ? t('debt') : solde > 0 ? t('credit') : t('settled')}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal nouveau client */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNew(false)} />
          <div className="relative w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-800">{t("new_client")}</h2>
              <button onClick={() => setShowNew(false)} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="space-y-3 mb-5">
              <input
                autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t("client_name_ph")}
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500"
              />
              <input
                value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder={t("phone") + " " + t("optional")}
                type="tel" inputMode="tel"
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500"
              />
            </div>
            <button onClick={createClient} disabled={saving || !form.name.trim()}
              className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 active:scale-95">
              {saving ? '...' : 'Ajouter ✓'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
